import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import { ApiError } from "@/api/apiClient";
import type { GraphBuildJob } from "@/api/graphBuilderApi";
import { getGraphBuildJobStatus } from "@/api/graphBuilderApi";

const STORAGE_KEY = "chatGraphBuildJobId";
const POLLING_TIMEOUT_MS = 500_000;

/**
 * Holds optional graph build job id and polls until completed, then calls onGraphGenerated.
 * Parent should set polling job id when send message returns jobId.
 * Returns both a setter for the job id and the latest jobStatus for UI feedback.
 */
export function useChatGraphBuildPolling(onGraphGenerated?: (graphId: string) => void) {
	const queryClient = useQueryClient();
	const [pollingJobId, setPollingJobIdState] = useState<string | null>(() => localStorage.getItem(STORAGE_KEY));

	const setPollingJobId = useCallback((jobId: string | null) => {
		setPollingJobIdState(jobId);
		if (jobId) localStorage.setItem(STORAGE_KEY, jobId);
		else localStorage.removeItem(STORAGE_KEY);
	}, []);

	const { data: jobStatus } = useQuery({
		queryKey: ["graphBuildJobStatus", pollingJobId],
		queryFn: ({ signal }) => {
			if (!pollingJobId) throw new Error("No job ID");
			return getGraphBuildJobStatus(pollingJobId, { signal });
		},
		enabled: !!pollingJobId,
		retry: (failureCount, error) => {
			if (error instanceof ApiError && error.status === 404) {
				setPollingJobId(null);
				return false;
			}
			return failureCount < 2;
		},
		refetchInterval: (query) => {
			const data = query.state.data as GraphBuildJob | undefined;
			if (data?.status === "completed" || data?.status === "failed") return false;
			if (data?.createdAt) {
				const elapsed = Date.now() - new Date(data.createdAt).getTime();
				if (elapsed >= POLLING_TIMEOUT_MS) return false;
			}
			return 2000;
		},
	});

	useEffect(() => {
		if (jobStatus?.status === "completed" && jobStatus.graphId && onGraphGenerated) {
			onGraphGenerated(jobStatus.graphId);
			queryClient.invalidateQueries({ queryKey: ["workspaces"] });
			queryClient.invalidateQueries({ queryKey: ["workspace"] });
			setPollingJobId(null);
		} else if (jobStatus?.status === "failed") {
			setPollingJobId(null);
		}
	}, [jobStatus, onGraphGenerated, queryClient, setPollingJobId]);

	return { setPollingJobId, jobStatus };
}
